import { useEffect, useRef, useState } from 'react';
import { Form, useFormikContext } from 'formik';
import { clearStep } from 'redux/slices/createTrial';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { createTrialSteps, guestCreateTrialSteps } from 'utils/createTrial';
import {
  parseShapefileSet,
  demoFieldBoundaryGeoJSON,
} from 'utils/ShapefileUtils';
import useAuth from 'hooks/useAuth';
import isEqual from 'utils/helpers/isEqual';
import Tooltip from 'components/global/Tooltip';
import FileUploadModal from '../FileUploadModal';
import Checkbox from 'components/global/Checkbox';
import Button from 'components/global/Button';
import FormErrorMessage from 'components/global/FormErrorMessage';
import ShapefileFormAndPreview from '../ShapefileFormAndPreview';
import styles from './trialBoundaryForm.module.scss';

const TrialBoundaryForm = () => {
  const { trial } = useAppSelector((state) => state.createTrial);
  const { values, setFieldValue, isSubmitting, errors, touched } =
    useFormikContext<Record<string, any>>();
  const { isGuest } = useAuth();
  const dispatch = useAppDispatch();

  const trialSteps = isGuest ? guestCreateTrialSteps : createTrialSteps;

  // Keep track of loading state for each boundary
  const [fieldBoundaryLoading, setFieldBoundaryLoading] = useState(false);
  const [trialBoundaryLoading, setTrialBoundaryLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  // Error state for displaying processing errors
  const [fieldBoundaryError, setFieldBoundaryError] = useState<string | null>(
    null,
  );
  const [trialBoundaryError, setTrialBoundaryError] = useState<string | null>(
    null,
  );
  const [demoError, setDemoError] = useState<string | null>(null);

  useEffect(() => {
    const addTrialFileNames = (metaName: string) => {
      if (trial[metaName]) setFieldValue(metaName, trial[metaName]);
    };

    addTrialFileNames('field_boundary_files_meta');
    addTrialFileNames('trial_boundary_files_meta');

    // Restore file state to 'trial' if we have metadata but files are null/empty
    // This ensures filenames persist when navigating back to this page
    if (
      trial.field_boundary_files_meta &&
      trial.field_boundary_files_meta.length > 0 &&
      (!values.field_boundary_files ||
        (Array.isArray(values.field_boundary_files) &&
          values.field_boundary_files.length === 0))
    ) {
      setFieldValue('field_boundary_files', 'trial');
    }

    if (
      trial.trial_boundary_files_meta &&
      trial.trial_boundary_files_meta.length > 0 &&
      (!values.trial_boundary_files ||
        (Array.isArray(values.trial_boundary_files) &&
          values.trial_boundary_files.length === 0))
    ) {
      setFieldValue('trial_boundary_files', 'trial');
    }
  }, [
    trial,
    setFieldValue,
    values.field_boundary_files,
    values.trial_boundary_files,
  ]);

  useEffect(() => {
    if (isGuest) {
      setFieldValue('trial_boundary_is_field_boundary', true);
    }
  }, [isGuest, setFieldValue]);

  const trialRef = useRef(trial);

  useEffect(() => {
    trialRef.current = trial;
  }, [trial]);

  useEffect(() => {
    //reset abline step once field boundary changes
    if (
      isSubmitting &&
      (!isEqual(values.field_boundary_geojson, trial.field_boundary_geojson) ||
        !isEqual(values.trial_boundary_geojson, trial.trial_boundary_geojson))
    ) {
      const ablineKeys = [
        'transactionId',
        'get_abline_method',
        'abline_rotation',
        'abline_adjust',
        'abline_heading',
        'abline_files_meta',
        'abline_geojson',
      ];
      const updatedTrial = { ...trialRef.current };

      for (const key of ablineKeys) {
        delete updatedTrial[key];
      }

      dispatch(
        clearStep({
          index: trialSteps.findIndex(({ heading }) => heading === 'AB Line'),
          data: updatedTrial,
        }),
      );
    }
  }, [
    isSubmitting,
    trial.field_boundary_geojson,
    trial.trial_boundary_geojson,
    values.field_boundary_geojson,
    values.trial_boundary_geojson,
    dispatch,
    trialSteps,
  ]);

  useEffect(() => {
    if (values.trial_boundary_is_field_boundary) {
      setFieldValue('trial_boundary_geojson', null);
    }
  }, [values.trial_boundary_is_field_boundary, setFieldValue]);

  // Process uploaded files and convert to GeoJSON
  const handleFieldBoundaryUpload = async (files: File[]) => {
    // Clear any previous errors
    setFieldBoundaryError(null);

    // Only store metadata about files, not the files themselves
    setFieldValue(
      'field_boundary_files_meta',
      files.map((file) => file.name),
    );

    // Convert files to GeoJSON immediately
    if (files.length > 0) {
      try {
        setFieldBoundaryLoading(true);
        const geojson = await parseShapefileSet(files);

        // Store only the GeoJSON, not the files themselves
        setFieldValue('field_boundary_geojson', geojson);

        // Set to 'trial' so FileUploadModalButton shows metadata filenames
        setFieldValue('field_boundary_files', 'trial');

        setFieldBoundaryLoading(false);
      } catch (error) {
        setFieldBoundaryError(
          error instanceof Error
            ? error.message
            : 'Error converting field boundary',
        );
        setFieldBoundaryLoading(false);
      }
    } else {
      setFieldValue('field_boundary_geojson', null);
    }
  };

  const handleTrialBoundaryUpload = async (files: File[]) => {
    // Clear any previous errors
    setTrialBoundaryError(null);

    // Only store metadata about files, not the files themselves
    setFieldValue(
      'trial_boundary_files_meta',
      files.map((file) => file.name),
    );

    if (values.trial_boundary_is_field_boundary) {
      setFieldValue('trial_boundary_geojson', null);
      return;
    }

    // Convert files to GeoJSON immediately
    if (files.length > 0) {
      try {
        setTrialBoundaryLoading(true);
        const geojson = await parseShapefileSet(files);

        // Store only the GeoJSON, not the files themselves
        setFieldValue('trial_boundary_geojson', geojson);

        // Set to 'trial' so FileUploadModalButton shows metadata filenames
        setFieldValue('trial_boundary_files', 'trial');

        setTrialBoundaryLoading(false);
      } catch (error) {
        setTrialBoundaryError(
          error instanceof Error
            ? error.message
            : 'Error converting trial boundary',
        );
        setTrialBoundaryLoading(false);
      }
    } else {
      setFieldValue('trial_boundary_geojson', null);
    }
  };

  // Handle demo field boundary
  const handleDemoFieldBoundary = async () => {
    try {
      // Clear any previous errors
      setDemoError(null);
      setDemoLoading(true);

      // Use the imported demo GeoJSON constant
      setFieldValue('field_boundary_geojson', demoFieldBoundaryGeoJSON);

      // Set metadata to indicate demo file was used
      setFieldValue('field_boundary_files_meta', [
        'demo_field__boundary.geojson',
      ]);

      // Set to 'trial' so FileUploadModalButton shows metadata filenames
      setFieldValue('field_boundary_files', 'trial');

      // If using field boundary as trial boundary, clear trial boundary
      if (values.trial_boundary_is_field_boundary) {
        setFieldValue('trial_boundary_geojson', null);
        setFieldValue('trial_boundary_files', null);
      }
      setDemoLoading(false);
    } catch (error) {
      setDemoError(
        error instanceof Error ? error.message : 'Error loading demo field',
      );
      setDemoLoading(false);
    }
  };

  const formContent = (
    <>
      <FileUploadModal
        name="field_boundary_files"
        metaName="field_boundary_files_meta"
        title="field boundary"
        fileDescription="Select the field boundary shapefile."
        uploadAction={handleFieldBoundaryUpload}
      />
      <div>
        <Button
          action="button"
          variant="brand"
          onClick={handleDemoFieldBoundary}
          className={styles['demo-button']}
          data-testid="demo-field-button"
        >
          Use Demo Field
        </Button>
      </div>

      {/* Display validation errors */}
      <div>
        {errors.field_boundary_geojson && touched.field_boundary_geojson && (
          <FormErrorMessage error={errors.field_boundary_geojson as string} />
        )}
      </div>

      {/* Display processing errors */}
      {fieldBoundaryError && (
        <div className={styles['error-message']}>
          <FormErrorMessage error={fieldBoundaryError} />
        </div>
      )}

      {demoError && (
        <div className={styles['error-message']}>
          <FormErrorMessage error={demoError} />
        </div>
      )}

      <div>
        <Tooltip
          label={
            isGuest
              ? 'Please create an account to use a trial boundary that is different from the field boundary. This functionality is disabled without a user account.'
              : ''
          }
        >
          <Checkbox
            id="trial_boundary_is_field_boundary"
            label="The trial boundary is the same as the field boundary"
            checked={values.trial_boundary_is_field_boundary || isGuest}
            onChange={() =>
              setFieldValue(
                'trial_boundary_is_field_boundary',
                !values.trial_boundary_is_field_boundary,
              )
            }
            disabled={isGuest}
          />
        </Tooltip>
      </div>

      <FileUploadModal
        name="trial_boundary_files"
        metaName="trial_boundary_files_meta"
        title="trial boundary"
        fileDescription="Select the trial boundary shapefile."
        isDisabled={values.trial_boundary_is_field_boundary || isGuest}
        uploadAction={handleTrialBoundaryUpload}
      />

      {/* Display validation errors */}
      {errors.trial_boundary_geojson &&
        touched.trial_boundary_geojson &&
        !values.trial_boundary_is_field_boundary && (
          <FormErrorMessage error={errors.trial_boundary_geojson as string} />
        )}

      {/* Display processing errors */}
      {trialBoundaryError && !values.trial_boundary_is_field_boundary && (
        <div className={styles['error-message']}>
          <FormErrorMessage error={trialBoundaryError} />
        </div>
      )}

      {(fieldBoundaryLoading || trialBoundaryLoading || demoLoading) && (
        <div className={styles['loading-indicator']}>
          {demoLoading ? 'Loading demo field...' : 'Converting shapefile...'}
        </div>
      )}
    </>
  );

  return (
    <Form className={styles['trial-boundary-form']}>
      <ShapefileFormAndPreview
        fieldBoundaryGeoJSON={values.field_boundary_geojson}
        trialBoundaryGeoJSON={
          values.trial_boundary_is_field_boundary
            ? undefined
            : values.trial_boundary_geojson
        }
      >
        {formContent}
      </ShapefileFormAndPreview>
    </Form>
  );
};

export default TrialBoundaryForm;
